/**
 * VISA/CERTS: Visa management section for helper profiles
 */

import React, { useState, useEffect } from 'react';
import { Globe, Plus, Edit2, Trash2, X, Check, AlertTriangle } from 'lucide-react';
import { VISA_TYPES, VISA_GROUPS, COMMON_VISAS, getVisaLabel, calculateVisaStatus, getStatusBadgeClass, getStatusLabel } from '../data/visaTypes';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

export default function VisaSection({ userId, readOnly = false }) {
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingVisa, setEditingVisa] = useState(null);
  const [formData, setFormData] = useState({
    visa_type: '',
    issuing_country: '',
    visa_number: '',
    issue_date: '',
    expiry_date: ''
  });
  const [saving, setSaving] = useState(false);

  // Fetch visas on mount
  useEffect(() => {
    fetchVisas();
  }, []);

  const fetchVisas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dockmate_token');
      const response = await fetch(`${API_URL}/crew/visas`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setVisas(data.data.visas || []);
      } else {
        setError(data.error?.message || 'Failed to fetch visas');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.visa_type) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('dockmate_token');
      const url = editingVisa
        ? `${API_URL}/crew/visas/${editingVisa.id}`
        : `${API_URL}/crew/visas`;
      const method = editingVisa ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        await fetchVisas();
        resetForm();
      } else {
        setError(data.error?.message || 'Failed to save visa');
      }
    } catch (err) {
      setError('Failed to save visa');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (visaId) => {
    if (!window.confirm('Delete this visa?')) return;

    try {
      const token = localStorage.getItem('dockmate_token');
      const response = await fetch(`${API_URL}/crew/visas/${visaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setVisas(visas.filter(v => v.id !== visaId));
      } else {
        setError(data.error?.message || 'Failed to delete visa');
      }
    } catch (err) {
      setError('Failed to delete visa');
    }
  };

  const handleEdit = (visa) => {
    setEditingVisa(visa);
    setFormData({
      visa_type: visa.visa_type,
      issuing_country: visa.issuing_country || '',
      visa_number: visa.visa_number || '',
      issue_date: visa.issue_date || '',
      expiry_date: visa.expiry_date || ''
    });
    setShowForm(true);
  };

  const handleQuickAdd = (visaCode) => {
    setEditingVisa(null);
    setFormData({
      visa_type: visaCode,
      issuing_country: '',
      visa_number: '',
      issue_date: '',
      expiry_date: ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingVisa(null);
    setFormData({
      visa_type: '',
      issuing_country: '',
      visa_number: '',
      issue_date: '',
      expiry_date: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <span className="spinner h-6 w-6" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
        <Globe className="h-5 w-5 inline mr-2" />
        Visas & Travel Documents
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 rounded-apple flex items-start">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Quick add buttons */}
      {!readOnly && !showForm && (
        <div className="flex flex-wrap gap-2 mb-4">
          {COMMON_VISAS.filter(v => !visas.find(visa => visa.visa_type === v)).map(visaCode => (
            <button
              key={visaCode}
              onClick={() => handleQuickAdd(visaCode)}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-navy-50 hover:bg-navy-100 text-navy-700 rounded-full border border-navy-200 transition-colors"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              {getVisaLabel(visaCode)}
            </button>
          ))}
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-full border border-primary-200 transition-colors"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Other
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && !readOnly && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-navy-50 rounded-apple border border-navy-200">
          <h3 className="text-sm font-semibold text-navy-900 mb-3">
            {editingVisa ? 'Edit Visa' : 'Add New Visa'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Visa Type */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Visa Type *
              </label>
              <select
                value={formData.visa_type}
                onChange={(e) => setFormData({ ...formData, visa_type: e.target.value })}
                className="input-field"
                required
              >
                <option value="">Select visa type...</option>
                {VISA_GROUPS.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {group.visas.map(code => (
                      <option key={code} value={code}>
                        {VISA_TYPES[code].label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Issuing Country */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Issuing Country
              </label>
              <input
                type="text"
                value={formData.issuing_country}
                onChange={(e) => setFormData({ ...formData, issuing_country: e.target.value })}
                className="input-field"
                placeholder="e.g. United States"
              />
            </div>

            {/* Visa Number */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Visa/Document Number
              </label>
              <input
                type="text"
                value={formData.visa_number}
                onChange={(e) => setFormData({ ...formData, visa_number: e.target.value })}
                className="input-field"
                placeholder="Optional"
              />
            </div>

            {/* Issue Date */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Issue Date
              </label>
              <input
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                className="input-field"
              />
            </div>

            {/* Expiry Date */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Expiry Date
              </label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                className="input-field max-w-xs"
              />
              <p className="text-xs text-navy-500 mt-1">
                Leave blank if no expiry or unknown
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-sm text-navy-600 hover:bg-navy-100 rounded-full transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.visa_type}
              className="btn-primary text-sm py-2 px-4"
            >
              {saving ? (
                <>
                  <span className="spinner h-4 w-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  {editingVisa ? 'Update' : 'Add'} Visa
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Visa List */}
      {visas.length === 0 ? (
        <p className="text-navy-500 text-sm py-4">
          No visas or travel documents added yet.
          {!readOnly && ' Click a button above to add your first visa.'}
        </p>
      ) : (
        <div className="space-y-3">
          {visas.map(visa => {
            const status = visa.calculated_status || calculateVisaStatus(visa.expiry_date);
            const typeInfo = visa.type_info || VISA_TYPES[visa.visa_type] || {};

            return (
              <div
                key={visa.id}
                className={`p-4 rounded-apple border ${
                  status === 'expired' ? 'border-red-200 bg-red-50' :
                  status === 'renewal_needed' ? 'border-yellow-200 bg-yellow-50' :
                  'border-navy-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-navy-900">
                        {typeInfo.label || visa.visa_type}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}>
                        {getStatusLabel(status)}
                      </span>
                    </div>
                    <p className="text-sm text-navy-500 mb-2">
                      {typeInfo.region && <span>{typeInfo.region}</span>}
                      {visa.issuing_country && <span> &bull; {visa.issuing_country}</span>}
                    </p>
                    <div className="flex flex-wrap gap-4 text-xs text-navy-600">
                      {visa.visa_number && (
                        <span>No: {visa.visa_number}</span>
                      )}
                      {visa.issue_date && (
                        <span>Issued: {formatDate(visa.issue_date)}</span>
                      )}
                      {visa.expiry_date && (
                        <span className={status === 'expired' ? 'text-red-600 font-medium' : ''}>
                          Expires: {formatDate(visa.expiry_date)}
                        </span>
                      )}
                    </div>
                  </div>

                  {!readOnly && (
                    <div className="flex items-center gap-1 ml-4">
                      <button
                        onClick={() => handleEdit(visa)}
                        className="p-2 text-navy-500 hover:text-navy-700 hover:bg-navy-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(visa.id)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
